/**
 * ArrivalKitDisplay — guest-facing arrival kit, rendered on the WiFi page
 * after the WiFi handshake is complete. Pulls the merged group+property
 * kit and renders free sections immediately, with locked teasers for the
 * reward sections that require a Homesbyli account.
 *
 * Powered by GET /api/arrival-kit/by-unit/:unitCode. Anonymous returns a
 * kit with `gated_fields_hidden:true` and the reward fields stripped;
 * signed-in users get the full kit.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  KeyRound, LogOut, Volume2, ParkingCircle, Trash2, MessageSquare,
  Coffee, Gift, MapPin, Star, Lock, Sparkles, Loader2,
} from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import GiftCardRecommendations from './giftcards/GiftCardRecommendations';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = '#1E3F70';
const PINE = '#1F5C42';
const GOLD = '#FFC93C';

const Section = ({ icon: Icon, title, children, testId }) => (
  <div className="rounded-xl ring-1 ring-gray-200 bg-white p-4" data-testid={testId}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4" style={{ color: NAVY }} />
      <h4 className="text-sm font-bold uppercase tracking-[0.12em]" style={{ color: NAVY }}>
        {title}
      </h4>
    </div>
    <div className="text-sm text-gray-700 whitespace-pre-line">{children}</div>
  </div>
);

const LockedTeaser = ({ icon: Icon, title, subtitle, testId }) => (
  <div
    className="rounded-xl p-4 ring-1 ring-amber-200 bg-amber-50/60 relative"
    data-testid={testId}
  >
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-4 h-4" style={{ color: '#7A5A00' }} />
      <h4 className="text-sm font-bold uppercase tracking-[0.12em] flex-1" style={{ color: '#7A5A00' }}>
        {title}
      </h4>
      <Lock className="w-3.5 h-3.5 text-amber-700" />
    </div>
    <p className="text-xs text-amber-900/80">{subtitle}</p>
  </div>
);

const ArrivalKitDisplay = ({ unitCode, customerToken }) => {
  const navigate = useNavigate();
  const [kit, setKit] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const headers = customerToken ? { Authorization: `Bearer ${customerToken}` } : {};
        const res = await axios.get(
          `${API_BASE}/arrival-kit/by-unit/${encodeURIComponent(unitCode)}`,
          { headers },
        );
        if (!cancelled) {
          setKit(res.data?.kit || {});
          setAuthenticated(!!res.data?.authenticated);
        }
      } catch {
        if (!cancelled) setKit({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [unitCode, customerToken]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: NAVY }} />
      </div>
    );
  }

  if (!kit || !kit.has_kit) {
    return null; // No kit configured; render nothing extra
  }

  const hasGated = kit.gated_fields_hidden;
  // For signed-in guests, the API returns the real `breakfast_vouchers_per_stay`.
  // For anonymous, the value lives in `gated_hints.breakfast_vouchers_per_stay`.
  const hints = kit.gated_hints || {};
  const vouchersPerStay = authenticated
    ? (kit.breakfast_vouchers_per_stay || 0)
    : (hints.breakfast_vouchers_per_stay || 0);
  const recs = kit.local_recommendations;
  const hasLockedRecs = !authenticated && hints.has_local_recommendations;
  const hasPromoCarrot = authenticated ? !!kit.promo_code_carrot : !!hints.has_promo_code;
  const hasReviewCarrot = authenticated ? !!kit.review_carrot : !!hints.has_review_carrot;

  const goSignup = () => {
    // Stash unit_code so the signup flow can deep-link the guest back to
    // /stay/:unitCode after they create their account.
    try { sessionStorage.setItem('post_signup_redirect', `/stay/${unitCode}`); } catch { /* ignore */ }
    navigate('/?openAuth=1');
  };

  return (
    <div className="mt-6 space-y-4" data-testid="arrival-kit-display">
      {/* Section header */}
      <div className="text-center pt-2 pb-1">
        <h3 className="text-lg font-bold" style={{ color: NAVY }}>
          {kit.property_nickname || 'Your stay'}
        </h3>
        {kit.host_first_name && (
          <p className="text-xs text-gray-600 mt-0.5">
            Hosted by {kit.host_first_name}
          </p>
        )}
        {kit.welcome_message && (
          <p className="text-sm text-gray-700 mt-3 italic px-2">
          &ldquo;{kit.welcome_message}&rdquo;
        </p>
        )}
      </div>

      {/* Free sections */}
      {kit.door_code && (
        <Section icon={KeyRound} title="Door / lockbox code" testId="kit-door-code">
          <span className="font-mono text-lg font-bold tracking-wider" style={{ color: NAVY }}>
            {kit.door_code}
          </span>
          <div className="text-xs text-gray-500 mt-1">Reminder only — code was also emailed at booking.</div>
        </Section>
      )}

      {kit.checkout_instructions && (
        <Section icon={LogOut} title="Check-out" testId="kit-checkout">
          {kit.checkout_instructions}
        </Section>
      )}

      {kit.parking_info && (
        <Section icon={ParkingCircle} title="Parking" testId="kit-parking">
          {kit.parking_info}
        </Section>
      )}

      {kit.trash_recycling && (
        <Section icon={Trash2} title="Trash & recycling" testId="kit-trash">
          {kit.trash_recycling}
        </Section>
      )}

      {kit.quiet_hours && (
        <Section icon={Volume2} title="Quiet hours" testId="kit-quiet-hours">
          {kit.quiet_hours}
        </Section>
      )}

      {kit.house_rules && (
        <Section icon={MessageSquare} title="House rules" testId="kit-house-rules">
          {kit.house_rules}
        </Section>
      )}

      {/* Rewards — authenticated view */}
      {authenticated && vouchersPerStay > 0 && (
        <div className="rounded-xl p-4 text-white" style={{ backgroundColor: PINE }} data-testid="kit-vouchers-unlocked">
          <div className="flex items-center gap-2 mb-1">
            <Coffee className="w-4 h-4" />
            <h4 className="text-sm font-bold uppercase tracking-[0.12em]">
              {vouchersPerStay} breakfast voucher{vouchersPerStay > 1 ? 's' : ''} included
            </h4>
          </div>
          <p className="text-xs text-white/90">
            Head to your dashboard to view your QR codes and the list of partner restaurants.
          </p>
          <Button
            onClick={() => navigate('/customer/dashboard')}
            className="mt-3 w-full"
            style={{ backgroundColor: '#fff', color: PINE }}
          >
            View my vouchers
          </Button>
        </div>
      )}

      {authenticated && recs && recs.length > 0 && (
        <Section icon={MapPin} title="Local recommendations" testId="kit-recs-unlocked">
          <div className="space-y-2">
            {recs.map((r, idx) => (
              <a
                key={idx}
                href={r.url || `https://www.google.com/maps/search/${encodeURIComponent(r.name)}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-start justify-between gap-2 rounded-md hover:bg-gray-50 p-2 -mx-2 transition"
              >
                <div>
                  <div className="font-semibold text-gray-900">{r.name}</div>
                  {r.type && <div className="text-xs text-gray-500">{r.type}</div>}
                </div>
                <Sparkles className="w-3.5 h-3.5 text-amber-600 mt-1" />
              </a>
            ))}
          </div>
        </Section>
      )}

      {/* Anonymous gated teasers */}
      {!authenticated && hasGated && (
        <div className="space-y-3" data-testid="kit-gated-section">
          <div className="rounded-2xl ring-2 ring-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 p-5 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-2" style={{ backgroundColor: GOLD }}>
              <Sparkles className="w-6 h-6" style={{ color: '#3A2A00' }} />
            </div>
            <h3 className="text-lg font-bold" style={{ color: NAVY }}>
              Unlock the rest of your kit
            </h3>
            <p className="text-sm text-gray-700 mt-1">
              Create a free Homesbyli account to claim your rewards.
            </p>
            <Button
              onClick={goSignup}
              className="mt-4 w-full font-bold py-6"
              style={{ backgroundColor: PINE, color: '#fff' }}
              data-testid="kit-signup-cta"
            >
              Sign up — unlocks everything below
            </Button>
            <p className="text-[10px] text-gray-500 mt-2">
              Free · No password needed via magic-link · 20 seconds
            </p>
          </div>

          {vouchersPerStay > 0 && (
            <LockedTeaser
              icon={Coffee}
              title={`${vouchersPerStay} free breakfast voucher${vouchersPerStay > 1 ? 's' : ''}`}
              subtitle="Scan-to-redeem at participating restaurants — covers up to CA$15 each."
              testId="kit-locked-breakfast"
            />
          )}

          {hasLockedRecs && (
            <LockedTeaser
              icon={MapPin}
              title="Local recommendations"
              subtitle="Curated by your host — bakeries, brunch, coffee, and hidden gems within walking distance."
              testId="kit-locked-recs"
            />
          )}

          {hasPromoCarrot ? (
            <LockedTeaser
              icon={Gift}
              title="15% off your next direct stay"
              subtitle="Sign up to claim your personal promo code — saves you ~CA$30 per night vs Airbnb / Booking.com."
              testId="kit-locked-promo"
            />
          ) : (
            <LockedTeaser
              icon={Gift}
              title="Save 15% on your next stay"
              subtitle="Book directly with us next time and skip the OTA service fees."
              testId="kit-locked-promo"
            />
          )}

          {hasReviewCarrot && (
            <LockedTeaser
              icon={Star}
              title="Leave a review, earn a credit"
              subtitle="Reward yourself for telling future guests about your stay."
              testId="kit-locked-review"
            />
          )}
        </div>
      )}

      {/* Gift card cross-sell — sits at the bottom of the kit so guests
          discover it organically after the in-stay reference material.
          Silent-on-empty so the layout stays clean for properties where
          admin hasn't featured anything yet. */}
      <div className="pt-2" data-testid="kit-giftcard-recs">
        <GiftCardRecommendations
          context={authenticated ? 'arrival_kit:authenticated' : 'arrival_kit:anonymous'}
          limit={3}
          country="CAN"
          variant="card"
          title="Treat yourself while you're here"
          subtitle="Pre-buy gift cards for Montreal restaurants and brands you'll love — instant delivery, up to 15% off face value."
        />
      </div>
    </div>
  );
};

export default ArrivalKitDisplay;
